import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarClock, X, Loader2, Check } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { toast } from 'sonner';

const todayStr = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};

export default function LateCheckoutModal({ open, onClose, properties = [], onCreate, editing = null, onUpdate }) {
  const isEdit = !!editing;
  const [propertyId, setPropertyId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [time, setTime] = useState('11:00');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setPropertyId(editing.propertyId || '');
        setDate(editing.date || todayStr());
        setTime(editing.time || '11:00');
        setNote(editing.note || '');
      } else {
        setPropertyId(''); setDate(todayStr()); setTime('11:00'); setNote('');
      }
    }
  }, [open, editing]);

  const close = () => { if (!saving) onClose(); };

  const submit = async () => {
    if (!isEdit && !propertyId) { toast.error('Choose a property'); return; }
    if (!date || !time) { toast.error('Pick a date and time'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await onUpdate(editing.id, { date, time, note: note.trim() });
        toast.success('Late checkout updated');
      } else {
        await onCreate({ propertyId, date, time, note: note.trim() || undefined });
        toast.success('Late checkout scheduled — cleaners notified');
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save late checkout');
    } finally {
      setSaving(false);
    }
  };

  const propName = editing?.propertyName || (properties.find((p) => p.id === propertyId)?.name);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 40, opacity: 0 }}
            className="relative w-full sm:max-w-md sm:m-4 bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
            data-testid="late-checkout-modal"
          >
            <div className="px-5 py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0"><CalendarClock size={18} /></div>
                <div className="min-w-0">
                  <h3 className="font-bold text-base leading-tight">{isEdit ? 'Edit late checkout' : 'Schedule late checkout'}</h3>
                  <p className="text-xs text-white/80 truncate">{isEdit ? 'Change the time or note' : 'The assigned cleaners are notified automatically'}</p>
                </div>
              </div>
              <button onClick={close} className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center flex-shrink-0" data-testid="late-checkout-close"><X size={16} /></button>
            </div>

            <div className="p-5 space-y-4">
              {!isEdit && (
                <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5 text-xs text-slate-500 leading-relaxed">
                  <span className="font-semibold text-slate-700">3 quick steps:</span> 1) choose the property · 2) pick the checkout date · 3) set the new checkout time.
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Property</label>
                {isEdit ? (
                  <div className="w-full h-12 rounded-xl bg-slate-100 border border-slate-200 px-3 flex items-center text-sm font-semibold text-slate-600">{propName}</div>
                ) : (
                  <Select value={propertyId} onValueChange={setPropertyId}>
                    <SelectTrigger data-testid="late-checkout-property" className="w-full h-12 rounded-xl bg-white border-slate-200 text-sm font-semibold text-slate-700">
                      <SelectValue placeholder="Select a property" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl max-h-64">
                      {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Checkout date</label>
                  <input type="date" value={date} min={todayStr()} onChange={(e) => setDate(e.target.value)} data-testid="late-checkout-date"
                    className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Checkout time</label>
                  <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="late-checkout-time"
                    className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Note <span className="text-slate-300 normal-case font-medium">· optional</span></label>
                <input type="text" value={note} maxLength={80} onChange={(e) => setNote(e.target.value)} placeholder="e.g. guest requested 1pm" data-testid="late-checkout-note"
                  className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none" />
              </div>

              <button onClick={submit} disabled={saving} data-testid="late-checkout-submit"
                className="w-full h-12 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.99] disabled:opacity-60">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                {saving ? 'Saving...' : (isEdit ? 'Save changes' : 'Schedule late checkout')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
